import React from 'react';
import type { FC } from 'react';
import type { VariantOf } from 'variant';
import { match } from 'variant';
import { useAsync } from '@wix/cli-ui-kit';
import type { GenerationData } from '../../../types.js';
import type {
  AppNameOptions,
  TemplateSource,
} from '../../../parse-command-options.js';
import { RegisteringApp } from '../../RegisteringApp.js';
import { AppRegistered } from '../../AppRegistered.js';
import { McpConfigResult } from '../../mcp/getMcpResult.js';
import { useAppCreation } from '../useAppCreation.js';

interface NewAppAutoFlowProps {
  template: TemplateSource;
  appName: VariantOf<typeof AppNameOptions, 'Provided'>;
  extendAppId?: string;
  onSubmit: (data: GenerationData) => void;
}

export const NewAppAutoFlow: FC<NewAppAutoFlowProps> = ({
  onSubmit,
  template: templateFromOptions,
  appName: appNameFromOptions,
  extendAppId,
}) => {
  const createApp = useAppCreation({ extendAppId });

  useAsync(async () => {
    const appCreationData = await createApp.execute({
      appName: appNameFromOptions.appName,
      packageName: appNameFromOptions.packageName,
      template: templateFromOptions,
      mcpConfigResult: McpConfigResult.Canceled(),
    });

    onSubmit(appCreationData);
  }, []);

  return match(createApp.status, {
    Error: () => null,
    Loading: () => <RegisteringApp />,
    Success: ({
      result: {
        appData: { appId, appName },
      },
    }) => <AppRegistered appId={appId} appName={appName} />,
    NotRequested: () => null,
  });
};
